import React, { useState, useEffect } from 'react';
import { collection, query, orderBy, onSnapshot, addDoc, serverTimestamp } from 'firebase/firestore';
import { db } from '../firebase';
import { Spinner } from '../components/UI';

export default function SharedNotesScreen({ user, child }) {
    const [notes, setNotes] = useState([]);
    const [newNote, setNewNote] = useState('');
    const [loading, setLoading] = useState(true);

    useEffect(() => {
        const notesPath = `children/${child.id}/notes`;
        
        const q = query(collection(db, notesPath), orderBy('createdAt', 'asc'));
        
        const unsubscribe = onSnapshot(q, (snapshot) => {
            const loadedNotes = [];
            snapshot.forEach((doc) => {
                loadedNotes.push({ id: doc.id, ...doc.data() });
            });
            setNotes(loadedNotes);
            setLoading(false);
            
            // Scroll to bottom after render
            setTimeout(() => {
                const scrollContainer = document.getElementById('notes-container');
                if (scrollContainer) scrollContainer.scrollTop = scrollContainer.scrollHeight;
            }, 100);
        });

        return () => unsubscribe();
    }, [child.id, child.ownerUid, user.uid]);

    const handleSend = async (e) => {
        e.preventDefault();
        if (!newNote.trim()) return;

        const notesPath = `children/${child.id}/notes`;

        try {
            await addDoc(collection(db, notesPath), {
                text: newNote,
                senderId: user.uid,
                senderName: user.username || user.email,
                senderRole: user.role || 'Pengguna',
                createdAt: serverTimestamp()
            });
            setNewNote('');
        } catch (error) {
            console.error("Error adding note: ", error);
            alert("Gagal mengirim catatan.");
        }
    };

    const getRoleColor = (role) => {
        if (role === 'Tenaga Kesehatan') return 'bg-rose-100 text-rose-800 border-rose-200';
        if (role === 'Guru PAUD') return 'bg-emerald-100 text-emerald-800 border-emerald-200';
        return 'bg-blue-100 text-blue-800 border-blue-200'; // Orang Tua
    };

    const getRoleIcon = (role) => {
        if (role === 'Tenaga Kesehatan') return '👩‍⚕️';
        if (role === 'Guru PAUD') return '👩‍🏫';
        return '👨‍👩‍👦'; // Orang Tua
    };

    if (loading) {
        return <div className="flex justify-center py-10"><Spinner /></div>;
    }

    return (
        <div className="flex flex-col h-[60vh] bg-white/60 backdrop-blur-2xl rounded-[1.5rem] border border-white shadow-[0_8px_30px_rgba(0,0,0,0.04)] overflow-hidden">
            {/* Header */}
            <div className="bg-white/80 border-b border-gray-100 px-4 py-3 flex items-center justify-between shadow-sm">
                <div className="flex items-center space-x-2">
                    <span className="text-xl">📒</span>
                    <h3 className="font-bold text-gray-800">Buku Penghubung</h3>
                </div>
            </div>

            {/* Chat Area */}
            <div id="notes-container" className="flex-1 overflow-y-auto p-4 space-y-4">
                {notes.length === 0 ? (
                    <div className="text-center py-10 text-gray-400">
                        <p className="text-4xl mb-2">👋</p>
                        <p className="text-sm">Belum ada catatan. Sapa guru atau orang tua untuk mulai berdiskusi!</p>
                    </div>
                ) : (
                    notes.map((note) => {
                        const isMe = note.senderId === user.uid;
                        const roleColor = getRoleColor(note.senderRole);
                        
                        return (
                            <div key={note.id} className={`flex flex-col ${isMe ? 'items-end' : 'items-start'}`}>
                                {!isMe && (
                                    <span className="text-[10px] text-gray-500 mb-1 ml-1 font-medium">
                                        {getRoleIcon(note.senderRole)} {note.senderName} ({note.senderRole})
                                    </span>
                                )}
                                <div 
                                    className={`
                                        max-w-[85%] px-4 py-2.5 rounded-2xl text-sm shadow-sm border
                                        ${isMe ? 'bg-indigo-600 text-white border-indigo-700 rounded-br-sm' : `${roleColor} rounded-bl-sm`}
                                    `}
                                >
                                    {note.text}
                                </div>
                                <span className="text-[9px] text-gray-400 mt-1">
                                    {note.createdAt?.toDate().toLocaleTimeString('id-ID', { hour: '2-digit', minute: '2-digit' })}
                                </span>
                            </div>
                        );
                    })
                )}
            </div>

            {/* Input Area */}
            <form onSubmit={handleSend} className="bg-white/90 border-t border-gray-100 p-3 flex space-x-2">
                <input 
                    type="text" 
                    value={newNote}
                    onChange={(e) => setNewNote(e.target.value)}
                    placeholder="Tulis catatan..." 
                    className="flex-1 bg-gray-50 border border-gray-200 rounded-xl px-4 py-2 text-sm focus:outline-none focus:ring-2 focus:ring-indigo-500 focus:border-transparent"
                />
                <button 
                    type="submit" 
                    disabled={!newNote.trim()}
                    className="bg-indigo-600 text-white w-10 h-10 rounded-xl flex items-center justify-center disabled:opacity-50 hover:bg-indigo-700 transition-colors shadow-sm"
                >
                    <svg xmlns="http://www.w3.org/2000/svg" className="h-5 w-5 transform rotate-90" viewBox="0 0 20 20" fill="currentColor">
                        <path d="M10.894 2.553a1 1 0 00-1.788 0l-7 14a1 1 0 001.169 1.409l5-1.429A1 1 0 009 15.571V11a1 1 0 112 0v4.571a1 1 0 00.725.962l5 1.428a1 1 0 001.17-1.408l-7-14z" />
                    </svg>
                </button>
            </form>
        </div>
    );
}
